import { Component, effect, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { StrideLogActivityOption } from '@mira-avant/mtlibs';
import { StrideReports } from '../../../../../services/stride-reports';

const PAGE_SIZE = 20;

@Component({
	selector: 'app-stride-log-activity-picker',
	standalone: true,
	imports: [CommonModule, ZorroModule],
	templateUrl: './stride-log-activity-picker.component.html',
	styleUrl: './stride-log-activity-picker.component.scss',
})
export class StrideLogActivityPickerComponent {
	public clientUuid = input<string | undefined>(undefined);
	public studentUuid = input<string | undefined>(undefined);
	public onSelectEmitter = output<StrideLogActivityOption>({ alias: 'onSelect' });

	public readonly PAGE_SIZE = PAGE_SIZE;
	public activities = signal<StrideLogActivityOption[]>([]);
	public totalCount = signal<number>(0);
	public page = signal<number>(1);
	public loading = signal<boolean>(false);
	public selected = signal<StrideLogActivityOption | undefined>(undefined);

	constructor(private strideReports: StrideReports) {
		effect(() => {
			const clientUuid = this.clientUuid();
			const studentUuid = this.studentUuid();

			this.selected.set(undefined);
			this.page.set(1);

			if (clientUuid && studentUuid) {
				this.load(clientUuid, studentUuid, 1, true);
			} else {
				this.activities.set([]);
				this.totalCount.set(0);
			}
		});
	}

	public async onPageChange(page: number): Promise<void> {
		const clientUuid = this.clientUuid();
		const studentUuid = this.studentUuid();
		if (!clientUuid || !studentUuid) {
			return;
		}

		this.page.set(page);
		await this.load(clientUuid, studentUuid, page, false);
	}

	private async load(clientUuid: string, studentUuid: string, page: number, autoSelectFirst: boolean): Promise<void> {
		this.loading.set(true);
		try {
			const result = await this.strideReports.getStrideLogActivities$(clientUuid, studentUuid, page);
			this.activities.set(result.activities);
			this.totalCount.set(result.totalCount);

			if (autoSelectFirst && result.activities.length > 0) {
				this.select(result.activities[0]);
			}
		} finally {
			this.loading.set(false);
		}
	}

	public select(activity: StrideLogActivityOption): void {
		this.selected.set(activity);
		this.onSelectEmitter.emit(activity);
	}

	public isSelected(activity: StrideLogActivityOption): boolean {
		const selected = this.selected();
		return !!selected && selected.activityUuid === activity.activityUuid && selected.take === activity.take;
	}
}
